"use client";

import { c as _c } from "react/compiler-runtime";
import React, { forwardRef, ReactNode } from "react";
import { MoveRight } from "lucide-react";
import { THEME_TOKENS } from "@/constants/theme-constants";
import ImageComponent from "@/components/ui/image";
type ButtonVariant = "default" | "custom";
type IconPosition = "left" | "right";
type ButtonProps = {
  text: string | ReactNode;
  icon?: string;
  iconAlt?: string;
  classNames?: string;
  onClick?: () => void;
  variant?: ButtonVariant;
  iconPosition?: IconPosition;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  arrow?: boolean;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  ariaPressed?: boolean;
  ariaCurrent?: "page" | "step" | "location" | "date" | "time" | boolean;
  id?: string;
};
export const UniversalButton = forwardRef<HTMLButtonElement, ButtonProps>((t0, ref) => {
  const $ = _c(29);
  const {
    text,
    icon,
    iconAlt,
    classNames: t1,
    onClick,
    variant: t2,
    iconPosition: t3,
    disabled: t4,
    type: t5,
    arrow: t6,
    ariaLabel,
    ariaExpanded,
    ariaPressed,
    ariaCurrent,
    id
  } = t0;
  const classNames = t1 === undefined ? "" : t1;
  const variant = t2 === undefined ? "default" : t2;
  const iconPosition = t3 === undefined ? "left" : t3;
  const disabled = t4 === undefined ? false : t4;
  const type = t5 === undefined ? "button" : t5;
  const arrow = t6 === undefined ? false : t6;
  const defaultClasses = `flex font-lora font-medium gap-2 border ${THEME_TOKENS.classes.primaryBorder} rounded-full
    hover:bg-white hover:${THEME_TOKENS.classes.primaryText} hover:border
    justify-center items-center ${THEME_TOKENS.classes.primaryBg}
    p-2 px-4 transition duration-300
    tracking-wider whitespace-nowrap text-white`;
  const buttonClasses = variant === "default" ? `${defaultClasses} ${classNames}` : `flex justify-center items-center ${classNames}`;
  const t7 = `${buttonClasses} ${THEME_TOKENS.classes.focusRing} active:scale-[0.98] transition-all touch-action-manipulation`;
  let t8;
  if ($[0] !== icon || $[1] !== iconAlt || $[2] !== iconPosition || $[3] !== text) {
    t8 = icon && iconPosition === "left" && <><div className="flex justify-end"><ImageComponent className="w-[18px] h-[18px] z-20" src={icon} width={18} height={18} sizes="18px" alt={iconAlt || (typeof text === "string" ? `${text} icon` : "button icon")} /></div><span className="static" /></>;
    $[0] = icon;
    $[1] = iconAlt;
    $[2] = iconPosition;
    $[3] = text;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  let t9;
  if ($[5] !== text) {
    t9 = <span className="flex justify-center">{text}</span>;
    $[5] = text;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  let t10;
  if ($[7] !== arrow) {
    t10 = arrow && <MoveRight className="w-4" strokeWidth={3} />;
    $[7] = arrow;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] !== icon || $[10] !== iconAlt || $[11] !== iconPosition || $[12] !== text) {
    t11 = icon && iconPosition === "right" && <span className="flex items-center justify-start"><ImageComponent className="w-4 h-4 relative z-20" src={icon} width={17} height={17} sizes="17px" alt={iconAlt || (typeof text === "string" ? `${text} icon` : "button icon")} /></span>;
    $[9] = icon;
    $[10] = iconAlt;
    $[11] = iconPosition;
    $[12] = text;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== ariaCurrent || $[15] !== ariaExpanded || $[16] !== ariaLabel || $[17] !== ariaPressed || $[18] !== disabled || $[19] !== id || $[20] !== onClick || $[21] !== ref || $[22] !== t10 || $[23] !== t11 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9 || $[27] !== type) {
    t12 = <button id={id} type={type} className={t7} onClick={onClick} disabled={disabled} ref={ref} aria-label={ariaLabel} aria-expanded={ariaExpanded} aria-pressed={ariaPressed} aria-current={ariaCurrent}>{t8}{t9}{t10}{t11}</button>;
    $[14] = ariaCurrent;
    $[15] = ariaExpanded;
    $[16] = ariaLabel;
    $[17] = ariaPressed;
    $[18] = disabled;
    $[19] = id;
    $[20] = onClick;
    $[21] = ref;
    $[22] = t10;
    $[23] = t11;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = type;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
});
UniversalButton.displayName = "UniversalButton";
export default UniversalButton;